/**
 * Honest three-band signalling. "Years of experience" bars mean nothing to a
 * reader; where a thing was actually used does.
 */
export type Level = "core" | "working" | "familiar";

export const LEVEL_COPY: Record<Level, { label: string; hint: string }> = {
  core: {
    label: "Core",
    hint: "Daily driver. Shipped production systems with it.",
  },
  working: {
    label: "Working",
    hint: "Shipped real work with it, comfortable at depth.",
  },
  familiar: {
    label: "Familiar",
    hint: "Used it in anger; would want a short ramp.",
  },
};

export type Skill = {
  name: string;
  level: Level;
  /** Where it was actually used. Shown on hover / focus. */
  where?: string;
};

export type SkillGroup = {
  title: string;
  /** One line on what this group is for. */
  note: string;
  skills: Skill[];
};

export const SKILL_GROUPS: SkillGroup[] = [
  {
    title: "Languages",
    note: "What I write in.",
    skills: [
      {
        name: "TypeScript",
        level: "core",
        where: "Qualtrics widgets, pengolabs.com, this site",
      },
      {
        name: "C#",
        level: "core",
        where: "Swarmhold: simulation, netcode, tooling",
      },
      {
        name: "JavaScript",
        level: "core",
        where: "Six years, every frontend role",
      },
      {
        name: "GDScript",
        level: "working",
        where: "Pier Pals, shipped to Steam",
      },
      {
        name: "SQL",
        level: "working",
        where: "Oracle PL/SQL at CDM Smith; Postgres on the studio site",
      },
      { name: "Kotlin", level: "working", where: "Brainy Cards MVP" },
    ],
  },
  {
    title: "Frontend",
    note: "Interfaces and the data inside them.",
    skills: [
      { name: "React", level: "core", where: "Primary framework since 2020" },
      {
        name: "D3.js",
        level: "core",
        where: "Two years on Qualtrics' data visualization team",
      },
      { name: "HTML / CSS", level: "core", where: "Every role" },
      {
        name: "AngularJS",
        level: "working",
        where: "Legacy surface at Qualtrics and CDM Smith",
      },
      {
        name: "React Native",
        level: "working",
        where: "Pebbl., shipped to the App Store",
      },
      {
        name: "Next.js",
        level: "working",
        where: "pengolabs.com",
      },
      {
        name: "WebGL2",
        level: "working",
        where: "The instanced swarm demo above",
      },
    ],
  },
  {
    title: "Real-time & systems",
    note: "Where the frame budget is the spec.",
    skills: [
      { name: "Unity 6", level: "core", where: "Swarmhold, since 2025" },
      {
        name: "Photon Fusion 2",
        level: "core",
        where: "Server-authoritative multiplayer, 1–4 players",
      },
      {
        name: "Burst / Jobs",
        level: "core",
        where: "Flow field, swarm simulation, render fill",
      },
      {
        name: "Performance profiling",
        level: "core",
        where: "Every number on this page is measured",
      },
      {
        name: "GPU instancing",
        level: "working",
        where: "Vertex-animation texture render path",
      },
      {
        name: "Spatial data structures",
        level: "working",
        where: "Uniform grids, spatial hashing, flow fields",
      },
      { name: "Godot", level: "working", where: "Pier Pals" },
      { name: "Steamworks", level: "working", where: "Two shipped titles" },
    ],
  },
  {
    title: "Backend & data",
    note: "The other half of full-stack.",
    skills: [
      {
        name: "State replication",
        level: "core",
        where:
          "Swarmhold's three tiers: authoritative, server-driven, deterministic",
      },
      {
        name: "Server-authoritative simulation",
        level: "core",
        where:
          "Swarmhold: authoritative host, prediction for what the player controls",
      },
      {
        name: "Deterministic simulation",
        level: "working",
        where: "Input-driven bulk tier, identical on every peer",
      },
      {
        name: "Node.js",
        level: "working",
        where:
          "Studio-site API: report intake with per-product keys, rate limits and ticket routing; telemetry ingest",
      },
      {
        name: "PostgreSQL",
        level: "working",
        where:
          "Swarmhold gameplay telemetry, batched into Neon from shipped builds",
      },
      {
        name: "Oracle PL/SQL",
        level: "working",
        where: "Government applications at CDM Smith",
      },
      { name: "Firestore", level: "working", where: "Brainy Cards" },
    ],
  },
  {
    title: "Infrastructure & tooling",
    note: "Getting it out the door and watching it after.",
    skills: [
      {
        name: "CI/CD",
        level: "core",
        where: "66% build-time cut at Qualtrics",
      },
      {
        name: "Jenkins",
        level: "working",
        where: "Pipeline rebuild at Qualtrics",
      },
      {
        name: "Docker",
        level: "working",
        where: "Containerized CI test runners",
      },
      {
        name: "Splunk",
        level: "working",
        where: "Led the observability migration",
      },
    ],
  },
  {
    title: "Beyond the code",
    note: "A two-person studio does not have specialists.",
    skills: [
      {
        name: "Systems architecture",
        level: "core",
        where: "284 design documents and counting",
      },
      {
        name: "Technical writing",
        level: "core",
        where: "Architecture and design docs as a working artifact",
      },
      {
        name: "Cross-functional delivery",
        level: "core",
        where: "Product, UX and two partner teams at Qualtrics",
      },
      {
        name: "Telemetry & observability",
        level: "working",
        where: "Crash reporting and structured incident reports",
      },
      {
        name: "Localization",
        level: "working",
        where: "Six languages shipped",
      },
    ],
  },
];
